import { BufferAttribute, BufferGeometry, Group, Mesh, Vector3 } from 'three/webgpu'
import WireMaterial from '../materials/WireMaterial'

export default class Wires extends Group {
	static defaults = {
		device: null,
		count: 3,
		resolution: 18,
		smoothing: 4,
		radial: 10,
		radius: 0.02,
		slack: 1.0,
		gravity: -24,
		damping: 0.9,
		iterations: 8,
		colors: [0x777777, 0x777777, 0x777777],
		castShadow: true,
		receiveShadow: true,
		mount: { y: -0.45, z: 0.0, spread: 0.1 },
		wall: { y: -0.8, z: -1.2, spread: 0.05 },
	}

	constructor(options = {}) {
		super()

		this.options = { ...Wires.defaults, ...options }
		this.device = this.options.device
		this.rings = (this.options.resolution - 1) * this.options.smoothing + 1
		this.#build()
		this.#scratch()
	}

	#build() {
		const { count, resolution, colors } = this.options

		this.ropes = []

		for (let i = 0; i < count; i++) {
			const material = new WireMaterial({ tint: colors[i % colors.length] })
			const geometry = this.#geometry()
			const mesh = new Mesh(geometry, material)

			mesh.frustumCulled = false
			mesh.castShadow = this.options.castShadow
			mesh.receiveShadow = this.options.receiveShadow
			this.add(mesh)

			this.ropes.push({
				points: Array.from({ length: resolution }, () => new Vector3()),
				previous: Array.from({ length: resolution }, () => new Vector3()),
				path: Array.from({ length: this.rings }, () => new Vector3()),
				normal: new Vector3(1, 0, 0),
				rest: 0,
				geometry,
				material,
			})
		}
	}

	#geometry() {
		const { radial } = this.options
		const vertices = this.rings * (radial + 1)
		const geometry = new BufferGeometry()
		const uv = new Float32Array(vertices * 2)
		const indices = []

		for (let i = 0; i < this.rings; i++) {
			for (let j = 0; j <= radial; j++) {
				const vertex = i * (radial + 1) + j

				uv[vertex * 2] = j / radial
				uv[vertex * 2 + 1] = i / this.options.smoothing

				if (i === this.rings - 1 || j === radial) continue

				const a = vertex
				const b = vertex + radial + 1

				indices.push(a, a + 1, b, a + 1, b + 1, b)
			}
		}

		geometry.setAttribute('position', new BufferAttribute(new Float32Array(vertices * 3), 3))
		geometry.setAttribute('normal', new BufferAttribute(new Float32Array(vertices * 3), 3))
		geometry.setAttribute('uv', new BufferAttribute(uv, 2))
		geometry.setIndex(indices)

		return geometry
	}

	#scratch() {
		const { radial } = this.options

		this.head = new Vector3()
		this.tail = new Vector3()
		this.origin = new Vector3()
		this.segment = new Vector3()
		this.drift = new Vector3()
		this.tangent = new Vector3()
		this.frame = new Vector3()
		this.binormal = new Vector3()
		this.pull = new Vector3(0, this.options.gravity, 0)
		this.cos = new Float32Array(radial + 1)
		this.sin = new Float32Array(radial + 1)

		for (let j = 0; j <= radial; j++) {
			this.cos[j] = Math.cos((j / radial) * Math.PI * 2)
			this.sin[j] = Math.sin((j / radial) * Math.PI * 2)
		}
	}

	#anchors(index) {
		const { count, mount, wall, slack } = this.options
		const rope = this.ropes[index]
		const offset = count === 1 ? 0 : (index / (count - 1)) * 2 - 1

		this.head.set(offset * mount.spread, mount.y, mount.z)
		this.device.wrapper.localToWorld(this.head)

		this.device.getWorldPosition(this.origin)
		this.tail.set(this.origin.x + offset * wall.spread, this.origin.y + wall.y, wall.z)

		rope.rest = (this.head.distanceTo(this.tail) * slack) / (rope.points.length - 1)
	}

	#seed(rope) {
		for (let i = 0; i < rope.points.length; i++) {
			const alpha = i / (rope.points.length - 1)

			rope.points[i].lerpVectors(this.head, this.tail, alpha)
			rope.previous[i].copy(rope.points[i])
		}
	}

	#simulate(rope, delta) {
		const { damping, iterations } = this.options
		const { points, previous } = rope
		const last = points.length - 1

		for (let i = 1; i < last; i++) {
			this.drift.subVectors(points[i], previous[i]).multiplyScalar(damping)
			previous[i].copy(points[i])
			points[i].add(this.drift).addScaledVector(this.pull, delta * delta)
		}

		points[0].copy(this.head)
		points[last].copy(this.tail)

		for (let iteration = 0; iteration < iterations; iteration++) {
			for (let i = 0; i < last; i++) {
				const a = points[i]
				const b = points[i + 1]

				this.segment.subVectors(b, a)

				const distance = this.segment.length() || 1e-6
				const correction = (distance - rope.rest) / distance
				const share = i === 0 ? 0 : i + 1 === last ? 1 : 0.5

				a.addScaledVector(this.segment, correction * share)
				b.addScaledVector(this.segment, -correction * (1 - share))
			}
		}
	}

	#smooth(rope) {
		const { smoothing } = this.options
		const { points, path } = rope
		const last = points.length - 1

		for (let i = 0; i < this.rings; i++) {
			const span = Math.min(Math.floor(i / smoothing), last - 1)
			const t = i / smoothing - span
			const p0 = points[Math.max(span - 1, 0)]
			const p1 = points[span]
			const p2 = points[span + 1]
			const p3 = points[Math.min(span + 2, last)]
			const target = path[i]

			target.set(
				this.#spline(p0.x, p1.x, p2.x, p3.x, t),
				this.#spline(p0.y, p1.y, p2.y, p3.y, t),
				this.#spline(p0.z, p1.z, p2.z, p3.z, t)
			)
		}
	}

	#spline(a, b, c, d, t) {
		const t2 = t * t
		const t3 = t2 * t

		return 0.5 * (2 * b + (c - a) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3)
	}

	#draw(rope) {
		const { radial, radius } = this.options
		const { path } = rope
		const positions = rope.geometry.attributes.position.array
		const normals = rope.geometry.attributes.normal.array

		for (let i = 0; i < this.rings; i++) {
			const point = path[i]

			this.tangent.subVectors(path[Math.min(i + 1, this.rings - 1)], path[Math.max(i - 1, 0)]).normalize()
			this.frame.copy(rope.normal).addScaledVector(this.tangent, -rope.normal.dot(this.tangent))

			if (this.frame.lengthSq() < 1e-8) this.frame.set(this.tangent.y, -this.tangent.x, 0)

			this.frame.normalize()
			this.binormal.crossVectors(this.tangent, this.frame)
			rope.normal.copy(this.frame)

			for (let j = 0; j <= radial; j++) {
				const vertex = (i * (radial + 1) + j) * 3
				const nx = this.frame.x * this.cos[j] + this.binormal.x * this.sin[j]
				const ny = this.frame.y * this.cos[j] + this.binormal.y * this.sin[j]
				const nz = this.frame.z * this.cos[j] + this.binormal.z * this.sin[j]

				positions[vertex] = point.x + nx * radius
				positions[vertex + 1] = point.y + ny * radius
				positions[vertex + 2] = point.z + nz * radius
				normals[vertex] = nx
				normals[vertex + 1] = ny
				normals[vertex + 2] = nz
			}
		}

		rope.geometry.attributes.position.needsUpdate = true
		rope.geometry.attributes.normal.needsUpdate = true
	}

	update(delta) {
		this.device.updateMatrixWorld()

		const step = Math.min(delta, 1 / 30)

		for (let i = 0; i < this.ropes.length; i++) {
			const rope = this.ropes[i]

			this.#anchors(i)

			if (!this.seeded) this.#seed(rope)

			this.#simulate(rope, step)
			this.#smooth(rope)
			this.#draw(rope)
		}

		this.seeded = true
	}

	dispose() {
		for (const rope of this.ropes) {
			rope.geometry.dispose()
			rope.material.dispose()
		}
	}
}
